/**
 * Anzeige der Übertragung.
 *
 * ADR 0001 fordert drei sichtbare Zustände: örtlich gespeichert, wird
 * übertragen, bestätigt. Sie stehen hier zusammen mit dem Netzzustand in der
 * Kopfzeile — der Nutzer soll jederzeit erkennen können, ob seine Meldung schon
 * beim Server ist, ohne dafür in ein Untermenü zu gehen.
 *
 * Drei Betriebszustände kommen hinzu: „Erneuter Versuch" (vorübergehender
 * Fehler), „Wartet" (Endpunkt fehlt oder Eingabe muss korrigiert werden) und
 * „Teilweise übertragen" (die Meldung ist beim Server, ein Anhang fehlt
 * endgültig). Der letzte ist rot, weil dabei Beweismaterial verloren geht —
 * und weil ihn niemand durch Warten aus der Welt schafft.
 *
 * Handoff 2b (Stand 2026-08) verlangt fünf Zustände — Auf dem Gerät/grau,
 * Wird übertragen/blau, Bestätigt/grün, Erneuter Versuch/orange, Wartet/gelb.
 * „Teilweise übertragen" kam danach hinzu, aus demselben Grund, aus dem der
 * Handoff selbst entstand: ein stiller Fehler wurde aufgedeckt, bei dem ein
 * fehlender Anhang als „bestätigt" durchging. Sechster Zustand, nicht im
 * Handoff vorgesehen, aber notwendig.
 */
import { useEffect, useState } from "react";

import { IconClock, IconCloudOff, IconUpload } from "@/components/icons";
import { Sheet } from "@/components/ui/Sheet";
import { Button } from "@/components/ui/Button";
import { StatusPill, type StatusTone } from "@/components/ui/StatusPill";
import { formatRelative, t } from "@/i18n";
import { cn } from "@/lib/cn";
import { discard, retryNow } from "@/lib/queue";
import { useQueue } from "@/lib/useQueue";
import { isOnline, subscribeNetwork } from "@/platform/network";
import type { OutboxState } from "@/platform/storage";

const STATE_LABEL: Record<OutboxState, string> = {
  local: t.queue.local,
  sending: t.queue.sending,
  confirmed: t.queue.confirmed,
  failed: t.queue.failed,
  blocked: t.queue.blocked,
  partial: t.queue.partial,
};

/** Farbtöne nach Kapitel 11.3: grau = noch nicht bewertet, blau = geplant, grün = erledigt. */
const STATE_TONE: Record<OutboxState, StatusTone> = {
  local: "grey",
  sending: "blue",
  confirmed: "green",
  failed: "orange",
  blocked: "yellow",
  partial: "red",
};

function useOnline(): boolean {
  const [online, setOnline] = useState(isOnline);
  useEffect(() => subscribeNetwork(setOnline), []);
  return online;
}

export function SyncIndicator({ className }: { className?: string }) {
  const queue = useQueue();
  const online = useOnline();
  const [open, setOpen] = useState(false);

  // Aus der Liste gezählt, nicht aus den Zuständen summiert: In der Liste steht
  // auch ein bestätigter Eintrag, an dem noch ein ungelesener Hinweis hängt.
  // Zählte man ihn nicht mit, bliebe die Plakette aus — und mit ihr die einzige
  // Möglichkeit, die Liste überhaupt zu öffnen.
  const openCount = queue.pending.length;

  // Nichts offen und Netz da: keine Anzeige. Ein dauerhaft grünes Häkchen wird
  // nach zwei Tagen nicht mehr gelesen und nimmt in der Kopfzeile Platz weg.
  // (Handoff 2b: "Sichtbar nur, wenn etwas offen ist oder das Netz fehlt.")
  if (openCount === 0 && online) return null;

  const tone: StatusTone = !online ? "grey" : queue.counts.sending > 0 ? "blue" : "orange";
  const label = !online
    ? t.queue.offline
    : queue.counts.sending > 0
      ? t.queue.sending
      : `${openCount} ${t.queue.pendingCount}`;
  // Uhr und nicht Haken für den letzten Zweig: Hier zählen die Einträge, die
  // örtlich liegen, gescheitert oder blockiert sind — daneben stand das
  // Erledigt-Zeichen und darunter „3 ausstehend". Nach Kapitel 44 muss der
  // Unterschied auch ohne Farbe erkennbar sein; das Sinnbild trägt die Aussage,
  // der Farbton verstärkt sie nur.
  const icon = !online ? <IconCloudOff /> : queue.counts.sending > 0 ? <IconUpload /> : <IconClock />;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${t.queue.title}: ${label}`}
        className={cn("flex min-h-touch items-center rounded-card px-1", className)}
      >
        <StatusPill tone={tone} icon={icon}>
          {label}
        </StatusPill>
      </button>

      <Sheet
        open={open}
        onOpenChange={setOpen}
        title={t.queue.detailTitle}
        description={online ? t.queue.online : t.queue.offline}
      >
        {queue.pending.length === 0 ? (
          <p className="text-[0.9375rem] text-ink-muted">{t.queue.empty}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {queue.pending.map((entry) => (
              <li key={entry.id} className="rounded-card border border-line bg-surface-raised p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusPill tone={STATE_TONE[entry.state]} size="sm">
                        {STATE_LABEL[entry.state]}
                      </StatusPill>
                      {entry.kind !== undefined && (
                        <StatusPill tone="grey" size="sm">
                          {t.queue.kind[entry.kind]}
                        </StatusPill>
                      )}
                    </div>

                    {/*
                     * Fachliche Kurzfassung statt Endpunktpfad. „POST
                     * /checklist-items/3f2a…" beantwortet die Frage nicht, die
                     * jemand am Ende eines Rundgangs hat: Was von dem, was ich
                     * eingegeben habe, ist noch nicht durch? Der Pfad bleibt
                     * darunter stehen — für den Fall, dass jemand eine
                     * Fehlermeldung weitermelden muss.
                     */}
                    {entry.title !== undefined && entry.title !== "" && (
                      <p className="mt-1.5 truncate text-[0.9375rem] font-medium text-ink">
                        {entry.title}
                      </p>
                    )}
                    <p className="font-mono text-[0.8125rem] text-ink-muted">
                      {entry.method} {entry.path}
                    </p>
                    <p className="text-[0.8125rem] text-ink-faint" data-numeric="">
                      {formatRelative(entry.createdAt)} · {t.queue.attempts}: {entry.attempts}
                    </p>
                  </div>
                </div>

                {entry.lastError !== null && (
                  <p className="mt-2 text-[0.8125rem] text-ink-muted">{entry.lastError}</p>
                )}

                {/*
                 * Die Anmerkungen des Servers zu den Anhängen. Sie stehen hier
                 * und nicht in einer Kurzmeldung, weil die Übertragung im
                 * Hintergrund läuft: Eine Meldung, die niemand sieht, weil das
                 * Telefon in der Tasche steckt, ist keine Meldung.
                 */}
                {(entry.notices ?? []).length > 0 && (
                  <div className="mt-2">
                    <p className="text-[0.8125rem] font-semibold text-ink">{t.queue.notices}</p>
                    <ul className="mt-0.5 flex list-disc flex-col gap-0.5 pl-4">
                      {(entry.notices ?? []).map((hinweis, stelle) => (
                        <li key={`${stelle}-${hinweis}`} className="text-[0.8125rem] text-ink-muted">
                          {hinweis}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-3 flex gap-2">
                  <Button
                    size="md"
                    variant="secondary"
                    onClick={() => void retryNow(entry.id)}
                    // Bei „teilweise übertragen" und „bestätigt" gibt es nichts
                    // zu wiederholen: Der fehlende Anhang ist aus dem Gerät
                    // verschwunden, der Rest ist angekommen. Ein anklickbarer
                    // Knopf würde Hoffnung machen, wo keine ist — bleibt der
                    // Weg „Verwerfen", der den Hinweis als gelesen abhakt.
                    disabled={
                      entry.state === "sending" ||
                      entry.state === "partial" ||
                      entry.state === "confirmed"
                    }
                  >
                    {t.queue.retryNow}
                  </Button>
                  <Button size="md" variant="ghost" onClick={() => void discard(entry.id)}>
                    {t.common.discard}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Sheet>
    </>
  );
}
